import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { t } from 'i18next';
import { CrudMode, TableActiveCell, TableEditableCells, ValidationFeedback } from '@ohpen/enso-ui';
import { NotificationInstance } from 'antd/es/notification/interface';

import { flattenObjectValues } from '@ohpen/react-utils';
import {
  ActionEntityType,
  ActionType,
  ChangeRequestResponse,
  ChangeRequestValidationResponse,
  ComplianceStatus,
  CompliancePermissionLevel,
  ercProfilesAddOrUpdateData,
  ercProfilesDeleteData,
  exchangeRatesAddOrUpdateData,
  exchangeRatesDeleteData,
  feeDefinitionsAddOrUpdateData,
  feeDefinitionsDeleteData,
  feeProfilesAddOrUpdateData,
  feeProfilesDeleteData,
  OhpenValidationError,
  productChangesAddOrUpdateData,
  productChangesDeleteData,
  changeRequestFlipBetweenCloneAndReplace,
  baseRatesAddOrUpdateData,
  interestRatesAddOrUpdateData,
  interestRatesDeleteData,
  baseRatesDeleteData,
  productExclusivitySetsAddOrUpdateData,
  productExclusivitySetsDeleteData,
  procurationFeeRulesAddOrUpdateData,
  procurationFeeRulesDeleteData,
  procurationFeeAssignmentsAddOrUpdateData,
  procurationFeeAssignmentsDeleteData,
  procurationFeesAddOrUpdateData,
  procurationFeesDeleteData,
  firmProcFeeAssignmentsAddOrUpdateData,
  firmProcFeeAssignmentsDeleteData,
  lenderFirmsAddOrUpdateData,
  lenderFirmsDeleteData,
  lendersAddOrUpdateData,
  intermediaryAddOrUpdateData,
  intermediaryDeleteData,
  changeRequestCreateDraft,
  changeRequestGetValidations,
  productChangesSubmitForReview,
  changeRequestApprove,
  changeRequestReject,
  changeRequestResubmissionRequired,
  ChangeRequestRowFeedback,
  ChangeRequestCellFeedback,
} from '@pmp/api';
import { ProductStatus } from '@pmp/types';
import { getApiErrorMessage, crudToActionMap, changeRequestNotificationMessage } from '@pmp/utils';
import {
  ercProfilesFlatColumns,
  exchangeRateChangeColumns,
  exclusiveSetsCRColumns,
  feeProfilesColumns,
  feesFlatColumns,
  ProductCrudData,
  productsColumns,
  baseRateChangeColumns,
  interestRateColumns,
  procurationFeeRuleColumns,
  procurationFeeCrColumns,
  procurationFeeAssignmentsCRColumns,
  firmProcurationFeeCRColumns,
  lenderFirmCRColumns,
  intermediaryCRColumns,
  lenderCRColumns,
  LenderFirmCRDataRow,
  lenderFirmPermissionColumnKey,
  lenderIntermediaryPermissionColumnKey,
  TradingAddressRow,
  OperationWhitelistRow,
  sortOperationsByType,
} from '@pmp/components';

import {
  CrudTableAction,
  GetColumnEditableByStatusProps,
  GetColumnEditableProps,
  GetEditableCellsProps,
  OnAddRowProps,
  OnApproveProps,
  OnBulkAdjustProps,
  OnCloneRowProps,
  OnDeleteRowProps,
  OnGoNextProps,
  OnGoPrevProps,
  OnOpenProps,
  OnRejectProps,
  OnRequestChangesProps,
  OnResendForApprovalProps,
  OnSaveProps,
  OnSendForApproval,
  WithAdditionalFields,
  WithIsUsed,
  WithRowNumber,
} from './interfaces';

import type { TableColumn } from '@ohpen/enso-ui';
dayjs.extend(isSameOrBefore);

export const disableEditAllKeys = ['originalCode', 'code', 'replacesCode', 'replacedByCode'];
export const editableIfActive = ['stressRate', 'endDate', 'gracePeriodEndDate', 'productCutoffDate', 'belongsToExclusivitySets'];
export const editableIfWithdrawn = ['gracePeriodEndDate', 'productCutoffDate', 'belongsToExclusivitySets'];
export const rateChangeColumnKeys = [
  'code',
  'originalCode',
  'name',
  'applicationTypes',
  'repaymentTypes',
  'minDurationInMonths',
  'maxDurationInMonths',
  'initialPeriodInMonths',
  'minLtv',
  'maxLtv',
  'ercProfileName',
  'rate',
  'stressRate',
  'startDate',
  'endDate',
  'replacesCode',
  'replacedByCode',
  'group',
  'surcharge',
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getEntityChangesAddOrUpdateData = (entityType: ActionEntityType | null): any => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const callsByEntity: Partial<Record<ActionEntityType, any>> = {
    [ActionEntityType.PRODUCTS]: productChangesAddOrUpdateData,
    [ActionEntityType.ERC_PROFILES]: ercProfilesAddOrUpdateData,
    [ActionEntityType.FEES]: feeDefinitionsAddOrUpdateData,
    [ActionEntityType.FEE_PROFILES]: feeProfilesAddOrUpdateData,
    [ActionEntityType.EXCHANGE_RATES]: exchangeRatesAddOrUpdateData,
    [ActionEntityType.BASE_RATES]: baseRatesAddOrUpdateData,
    [ActionEntityType.INTEREST_RATES]: interestRatesAddOrUpdateData,
    [ActionEntityType.EXCLUSIVITY_SETS]: productExclusivitySetsAddOrUpdateData,
    [ActionEntityType.PROCURATION_FEE_RULES]: procurationFeeRulesAddOrUpdateData,
    [ActionEntityType.PROCURATION_FEES]: procurationFeesAddOrUpdateData,
    [ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS]: procurationFeeAssignmentsAddOrUpdateData,
    [ActionEntityType.FIRM_PROCURATION_FEE_ASSIGNMENTS]: firmProcFeeAssignmentsAddOrUpdateData,
    [ActionEntityType.LENDER_FIRMS]: lenderFirmsAddOrUpdateData,
    [ActionEntityType.LENDERS]: lendersAddOrUpdateData,
    [ActionEntityType.BROKERS]: intermediaryAddOrUpdateData,
  };
  return (entityType && callsByEntity[entityType]) ?? null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getEntityChangesDeleteData = (entityType: ActionEntityType | null): any => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const callsByEntity: Partial<Record<ActionEntityType, any>> = {
    [ActionEntityType.PRODUCTS]: productChangesDeleteData,
    [ActionEntityType.ERC_PROFILES]: ercProfilesDeleteData,
    [ActionEntityType.FEES]: feeDefinitionsDeleteData,
    [ActionEntityType.FEE_PROFILES]: feeProfilesDeleteData,
    [ActionEntityType.EXCHANGE_RATES]: exchangeRatesDeleteData,
    [ActionEntityType.BASE_RATES]: baseRatesDeleteData,
    [ActionEntityType.INTEREST_RATES]: interestRatesDeleteData,
    [ActionEntityType.EXCLUSIVITY_SETS]: productExclusivitySetsDeleteData,
    [ActionEntityType.PROCURATION_FEE_RULES]: procurationFeeRulesDeleteData,
    [ActionEntityType.PROCURATION_FEES]: procurationFeesDeleteData,
    [ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS]: procurationFeeAssignmentsDeleteData,
    [ActionEntityType.FIRM_PROCURATION_FEE_ASSIGNMENTS]: firmProcFeeAssignmentsDeleteData,
    [ActionEntityType.LENDER_FIRMS]: lenderFirmsDeleteData,
    [ActionEntityType.BROKERS]: intermediaryDeleteData,
  };
  return (entityType && callsByEntity[entityType]) ?? null;
};

type ChangeDataValidation = { isValid: boolean; rowValidations: ValidationFeedback };

const collectRemainingValidationPages = (
  actionId: string,
  isValid: boolean,
  previousRowValidations: ValidationFeedback,
  paginationToken?: string | null,
): Promise<ChangeDataValidation> =>
  paginationToken
    ? changeRequestGetValidations({ path: { changeRequestId: actionId }, query: { paginationToken } }).then(
        ({ data }: { data: ChangeRequestValidationResponse }) =>
          collectRemainingValidationPages(
            actionId,
            isValid,
            { ...previousRowValidations, ...data.rowValidations },
            data.pageOptions.paginationToken,
          ),
      )
    : Promise.resolve({ isValid, rowValidations: previousRowValidations });

// One chain of pending writes per change request. Rows are stored without versioning and every write
// answers with the whole change request's validation, so two writes in flight let the slower one
// overwrite the newer row and land its stale validation last.
const changeDataWrites = new Map<string, { tail: Promise<unknown>; queued: number }>();

const writeChangeData = (
  actionId: string,
  write: () => Promise<{ data: ChangeRequestValidationResponse }>,
): Promise<ChangeDataValidation> => {
  const chain = changeDataWrites.get(actionId) ?? { tail: Promise.resolve(), queued: 0 };
  chain.queued += 1;
  changeDataWrites.set(actionId, chain);

  const release = () => {
    chain.queued -= 1;
    if (chain.queued === 0) changeDataWrites.delete(actionId);
  };

  const validation = chain.tail.then(write, write).then(({ data }: { data: ChangeRequestValidationResponse }) =>
    collectRemainingValidationPages(actionId, data.isValid, data.rowValidations, data.pageOptions.paginationToken),
  );
  // Released as the chain's own continuation, which runs before the caller's, so the caller sees only
  // the writes that really are still queued behind it.
  chain.tail = validation.then(release, release);

  return validation;
};

const settleValidating = <EntityCrudData>(actionId: string, dispatch: (value: CrudTableAction<EntityCrudData>) => void) =>
  dispatch({ type: 'setIsValidating', isValidating: changeDataWrites.has(actionId) });

const dispatchValidation = <EntityCrudData>(
  actionId: string,
  dispatch: (value: CrudTableAction<EntityCrudData>) => void,
  { isValid, rowValidations }: ChangeDataValidation,
) => {
  dispatch({ type: 'setFeedback', validationFeedback: rowValidations, isValid });
  settleValidating(actionId, dispatch);
};

export const onOpen = <EntityCrudData, EntityChangeDataRow>({
  actionId,
  additionalFieldsKeys,
  data,
  dispatch,
  entityType,
  mode,
  cloneType,
  notification,
  handleOnClose,
  originalData,
  reviewInfo,
  t,
  validationFeedback,
}: OnOpenProps<EntityCrudData>) => {
  const entityChangesAddOrUpdateData = getEntityChangesAddOrUpdateData(entityType);

  if (!mode || !entityChangesAddOrUpdateData || !entityType || (mode !== CrudMode.CREATE && !data)) {
    notification().error({ title: t('global.errors.unexpected') });
    handleOnClose();
    return;
  }

  if (
    actionId &&
    (mode === CrudMode.VIEW ||
      mode === CrudMode.VIEW_RESULT ||
      mode === CrudMode.REVIEW ||
      mode === CrudMode.UPDATE_CHANGE ||
      mode === CrudMode.UPDATE_IMPORT_CHANGE)
  ) {
    dispatch({
      type: 'openReview',
      dataSource: data ?? [],
      originalDataSource: originalData ?? [],
      actionId,
      validationFeedback,
      reviewInfo,
    });
    return;
  }

  const dataSource = data ?? [{ rowNumber: 0 } as EntityCrudData];
  const actionType = crudToActionMap[mode] as ActionType;

  dispatch({ type: 'open', dataSource, originalDataSource: originalData ?? JSON.parse(JSON.stringify(dataSource)), cloneType });

  changeRequestCreateDraft({ body: { actionEntityType: entityType, actionType } })
    .then(({ data: { actionId } }: { data: ChangeRequestResponse }) => {
      const entityChangeDataRows: EntityChangeDataRow[] = unflattenEntityCrudAdditionalFields(dataSource, additionalFieldsKeys);
      const dataRows = entityChangeDataRows;

      if (!actionId) return Promise.reject(new Error('No actionId'));
      dispatch({ type: 'setActionId', actionId });
      let dataToSend = dataRows;
      if (entityType === ActionEntityType.LENDER_FIRMS) {
        dataToSend = nestLenderFirmAddress(dataToSend);
      }
      if (entityType === ActionEntityType.LENDERS) {
        dataToSend = nestLenderDraft(dataToSend);
      }
      return writeChangeData(actionId, () =>
        entityChangesAddOrUpdateData({ path: { actionId: actionId }, body: { data: dataToSend } }),
      ).then((validation: ChangeDataValidation) => dispatchValidation(actionId, dispatch, validation));
    })
    .catch((error: Error) => {
      notification().error({ title: getApiErrorMessage(error) });
      console.error(error);
      handleOnClose();
    })
    .finally(() => dispatch({ type: 'setIsLoading', isLoading: false }));
};

// Fields that must be cleared when another field changes because their value depends on it
const DEPENDENT_FIELD_RESETS: Partial<Record<ActionEntityType, Record<string, string[]>>> = {
  [ActionEntityType.BROKERS]: { firmId: ['tradingAddressId', 'advisers', 'assistants'] },
};

const UPPERCASE_ID_ENTITIES = new Set<ActionEntityType>([
  ActionEntityType.PRODUCTS,
  ActionEntityType.ERC_PROFILES,
  ActionEntityType.FEES,
  ActionEntityType.FEE_PROFILES,
  ActionEntityType.BASE_RATES,
]);

export const onSave = <EntityCrudData, ProductChangeDataRow>({
  mode,
  actionId,
  additionalFieldsKeys,
  cloneType,
  dataIndex,
  dataSource,
  disableCancel,
  dispatch,
  entityType,
  goToNext,
  notification,
  handleOnClose,
  rowNumbers,
  skipNext,
  value,
}: OnSaveProps<EntityCrudData, ProductChangeDataRow>): Promise<void> => {
  const entityChangesAddOrUpdateData = getEntityChangesAddOrUpdateData(entityType);

  if (!entityType || !actionId || !dataSource || !entityChangesAddOrUpdateData) return Promise.resolve();
  dispatch({ type: 'setIsValidating', isValidating: true });
  // If cloneType exists, we need to save all rows with new originalCode values
  const newData: WithRowNumber<EntityCrudData>[] =
    entityType === ActionEntityType.PRODUCTS && cloneType
      ? ((dataSource?.map(row => {
          let replacesCodeValue = '';
          if (cloneType !== 'new' && typeof row === 'object' && row && 'originalCode' in row) {
            replacesCodeValue = ((row as Record<string, unknown>).originalCode as string) ?? '';
          }
          return {
            ...row,
            replacesCode: replacesCodeValue,
          };
        }) as WithRowNumber<EntityCrudData>[]) ?? [])
      : [...dataSource];

  const idKey = entityType ? entityKeysMap[entityType]?.id : undefined;
  const normalizedValue =
    !!entityType && UPPERCASE_ID_ENTITIES.has(entityType) && dataIndex === idKey && typeof value === 'string'
      ? value.trim().toUpperCase()
      : value;

  // Array of rows that have changed
  const updatedRows: EntityCrudData[] = cloneType
    ? newData
    : rowNumbers.reduce((arr: EntityCrudData[], rowNumber: number) => {
        const row: EntityCrudData | undefined = newData.find(row => row.rowNumber === rowNumber);
        if (!row) return arr;

        // If the value is the same as the current value, do not update the row
        if (row[dataIndex as keyof EntityCrudData] === normalizedValue) return arr;
        // If the value has changed, update the cell value
        row[dataIndex as keyof EntityCrudData] = normalizedValue as never;
        // Clear any fields whose value depends on the one that just changed.
        for (const dependentKey of DEPENDENT_FIELD_RESETS[entityType]?.[dataIndex] ?? []) {
          row[dependentKey as keyof EntityCrudData] = null as never;
        }
        return [...arr, row];
      }, []);

  if (!updatedRows.length) {
    skipNext ? dispatch({ type: 'setActiveCell', activeCell: null }) : goToNext();
    settleValidating(actionId, dispatch);
    return Promise.resolve();
  }

  dispatch({ type: 'setDataSource', dataSource: newData });

  skipNext ? dispatch({ type: 'setActiveCell', activeCell: null }) : goToNext();

  const dataRows = unflattenEntityCrudAdditionalFields(updatedRows, additionalFieldsKeys);
  let dataToSend = dataRows;
  if (entityType === ActionEntityType.LENDER_FIRMS) {
    dataToSend = nestLenderFirmAddress(dataToSend);
    dataToSend = nestLenderFirmProcFeeBoost(dataToSend);
  }
  if (entityType === ActionEntityType.LENDERS) {
    dataToSend = nestLenderDraft(dataToSend);
  }

  return writeChangeData(actionId, () =>
    entityChangesAddOrUpdateData({
      path: { actionId: actionId },
      body: { data: dataToSend },
    }),
  )
    .then((validation: ChangeDataValidation) => {
      dispatchValidation(actionId, dispatch, validation);
      if (!disableCancel && (mode === CrudMode.UPDATE_CHANGE || mode === CrudMode.UPDATE_IMPORT_CHANGE)) {
        dispatch({ type: 'setDisableCancel' });
      }
    })
    .catch((err: OhpenValidationError) => {
      notification().error({ title: getApiErrorMessage(err) });
      console.error(err);
      settleValidating(actionId, dispatch);
      handleOnClose();
    })
    .finally(() => {
      return Promise.resolve();
    });
};

export const onBulkAdjust = <EntityCrudData>({
  actionId,
  additionalFieldsKeys,
  dataIndex,
  dataSource,
  dispatch,
  entityType,
  notification,
  t,
  changes,
}: OnBulkAdjustProps<EntityCrudData>): Promise<{ success: boolean; message?: string } | void> => {
  const entityChangesAddOrUpdateData = getEntityChangesAddOrUpdateData(entityType);

  if (!entityType || !actionId || !dataSource || !entityChangesAddOrUpdateData || !changes.length) return Promise.resolve();

  const newValuesByRowNumber = new Map(changes.map(({ row, newValue }) => [row.rowNumber, newValue]));
  const newData: WithRowNumber<EntityCrudData>[] = dataSource.map(row =>
    newValuesByRowNumber.has(row.rowNumber) ? { ...row, [dataIndex]: newValuesByRowNumber.get(row.rowNumber) } : row,
  );
  const updatedRows = newData.filter(row => newValuesByRowNumber.has(row.rowNumber));

  dispatch({ type: 'setIsValidating', isValidating: true });
  dispatch({ type: 'setDataSource', dataSource: newData });

  const dataRows = unflattenEntityCrudAdditionalFields(updatedRows, additionalFieldsKeys);
  let dataToSend = dataRows;
  if (entityType === ActionEntityType.LENDER_FIRMS) {
    dataToSend = nestLenderFirmAddress(dataToSend);
    dataToSend = nestLenderFirmProcFeeBoost(dataToSend);
  }
  if (entityType === ActionEntityType.LENDERS) {
    dataToSend = nestLenderDraft(dataToSend);
  }

  return writeChangeData(actionId, () =>
    entityChangesAddOrUpdateData({
      path: { actionId: actionId },
      body: { data: dataToSend },
    }),
  )
    .then((validation: ChangeDataValidation) => dispatchValidation(actionId, dispatch, validation))
    .catch((err: OhpenValidationError) => {
      notification().error({ message: t('global.errors.unexpected') });
      console.error(err);
      settleValidating(actionId, dispatch);
      return { success: false, message: t('global.errors.unexpected') };
    });
};

export const onSendForApproval = <EntityCrudData>({
  actionId,
  cloneType,
  dispatch,
  mode,
  notification,
  handleOnClose,
  t,
  resetSelectedRows,
  refreshTable,
  enableFourEyes,
}: OnSendForApproval<EntityCrudData>) => {
  if (!actionId) {
    notification().error({ title: t('global.errors.unexpected') });
    return;
  }

  dispatch({ type: 'setIsLoading', isLoading: true });

  productChangesSubmitForReview({ path: { actionId: actionId } })
    .then(() => {
      notification().success({ title: changeRequestNotificationMessage(t, enableFourEyes) });
      handleOnClose();
      resetSelectedRows?.();
      if (
        mode &&
        ([CrudMode.EDIT, CrudMode.BULK_EDIT].includes(mode) ||
          (cloneType === 'replacement' && [CrudMode.CLONE, CrudMode.BULK_CLONE].includes(mode))) &&
        refreshTable
      ) {
        refreshTable();
      }
    })
    .catch(error => {
      notification().error({ title: getApiErrorMessage(error) });
    })
    .finally(() => dispatch({ type: 'setIsLoading', isLoading: false }));
};

export const onResendForApproval = <EntityCrudData>({
  actionId,
  dispatch,
  notification,
  handleOnClose,
  t,
  resetSelectedRows,
  updateTable,
  valueLists,
}: OnResendForApprovalProps<EntityCrudData>) => {
  if (!actionId) {
    notification().error({ title: t('global.errors.unexpected') });
    return;
  }

  dispatch({ type: 'setIsLoading', isLoading: true });

  productChangesSubmitForReview({ path: { actionId: actionId } })
    .then(() => {
      notification().success({ title: t('global.notifications.changeSentForReApproval') });
      updateTable?.({ actionId, status: { key: 'PENDING_REVIEW', value: valueLists?.ACTION_STATUS.PENDING_REVIEW } });
      handleOnClose();
      resetSelectedRows?.();
    })
    .catch(error => {
      notification().error({ title: getApiErrorMessage(error) });
    })
    .finally(() => dispatch({ type: 'setIsLoading', isLoading: false }));
};

export const onApprove = <EntityCrudData>({
  actionId,
  dispatch,
  notification,
  handleOnClose,
  t,
  updateTable,
  valueLists,
}: OnApproveProps<EntityCrudData>) => {
  if (!actionId) {
    notification().error({ title: t('global.errors.unexpected') });
    return;
  }

  dispatch({ type: 'setIsLoading', isLoading: true });

  changeRequestApprove({ path: { changeRequestId: actionId }, body: {} })
    .then(() => {
      notification().success({ title: t('global.notifications.changeApproved') });
      updateTable?.({ actionId, status: { key: 'APPROVED', value: valueLists?.ACTION_STATUS.APPROVED } });
      handleOnClose();
    })
    .catch(error => {
      notification().error({ title: getApiErrorMessage(error) });
    })
    .finally(() => dispatch({ type: 'setIsLoading', isLoading: false }));
};

export const onReject = <EntityCrudData>({
  actionId,
  dispatch,
  form,
  notification,
  handleOnClose,
  t,
  updateTable,
  valueLists,
}: OnRejectProps<EntityCrudData>) => {
  if (!actionId) {
    notification().error({ title: t('global.errors.unexpected') });
    return;
  }
  form
    .validateFields()
    .then(({ comment }: { comment: string }) => {
      dispatch({ type: 'setIsLoading', isLoading: true });
      changeRequestReject({ path: { changeRequestId: actionId }, body: { comment } })
        .then(() => {
          notification().success({ title: t('global.notifications.changeRejected') });
          updateTable?.({ actionId, status: { key: 'REJECTED', value: valueLists?.ACTION_STATUS.REJECTED } });
          handleOnClose();
        })
        .catch(error => {
          notification().error({ title: getApiErrorMessage(error) });
        })
        .finally(() => dispatch({ type: 'setIsLoading', isLoading: false }));
    })
    .catch(() => {
      return;
    });
};

export const onRequestChanges = <EntityCrudData>({
  actionId,
  dispatch,
  form,
  notification,
  handleOnClose,
  t,
  updateTable,
  valueLists,
}: OnRequestChangesProps<EntityCrudData>) => {
  if (!actionId) {
    notification().error({ title: t('global.errors.unexpected') });
    return;
  }
  form
    .validateFields()
    .then(({ comment }: { comment: string }) => {
      dispatch({ type: 'setIsLoading', isLoading: true });
      changeRequestResubmissionRequired({ path: { changeRequestId: actionId }, body: { comment } })
        .then(() => {
          notification().success({ title: t('global.notifications.requestChanges') });
          updateTable?.({ actionId, status: { key: 'PENDING_RESUBMISSION', value: valueLists?.ACTION_STATUS.PENDING_RESUBMISSION } });
          handleOnClose();
        })
        .catch(error => {
          notification().error({ title: getApiErrorMessage(error) });
        })
        .finally(() => dispatch({ type: 'setIsLoading', isLoading: false }));
    })
    .catch(() => {
      return;
    });
};

export const onAddRow = <EntityCrudData>({ actionId, dataSource, dispatch, notification, t }: OnAddRowProps<EntityCrudData>) => {
  if (!actionId || !dataSource) {
    notification().error({ title: t('global.errors.unexpected') });
    return;
  }
  const rowNumber = dataSource[dataSource?.length - 1].rowNumber + 1;
  const newRow: EntityCrudData = { rowNumber } as EntityCrudData;
  const newDataSource = [...(dataSource ?? []), newRow];

  dispatch({ type: 'setIsValidating', isValidating: true });

  writeChangeData(actionId, () => productChangesAddOrUpdateData({ path: { actionId: actionId }, body: { data: [{ rowNumber }] } }))
    .then((validation: ChangeDataValidation) => {
      dispatch({ type: 'setDataSource', dataSource: newDataSource });
      dispatchValidation(actionId, dispatch, validation);
    })
    .catch((err: OhpenValidationError) => {
      notification().error({ title: getApiErrorMessage(err) });
      console.error(err);
      settleValidating(actionId, dispatch);
    });
};

export const onCloneRow = <EntityCrudData>({
  actionId,
  additionalFieldsKeys,
  dataSource,
  entityType,
  record,
  columnsToClean,
  notification,
  t,
  dispatch,
}: OnCloneRowProps<EntityCrudData>) => {
  const entityChangesAddOrUpdateData = getEntityChangesAddOrUpdateData(entityType);

  if (!actionId || !dataSource || !entityChangesAddOrUpdateData) {
    notification().error({ title: t('global.errors.unexpected') });
    return;
  }
  const { activeColumnKey, rowFeedback, ...data } = record as EntityCrudData & {
    activeColumnKey?: string;
    rowFeedback?: ValidationFeedback;
  };
  const rowNumber = dataSource[dataSource.length - 1].rowNumber + 1;
  const cleanedData = { ...data };
  columnsToClean.forEach(key => {
    (cleanedData as Record<string, unknown>)[key as string] = null;
  });
  const newRow: EntityCrudData = { ...cleanedData, rowNumber } as EntityCrudData;
  const newDataSource = [...dataSource, newRow];

  const cloneDataRows = unflattenEntityCrudAdditionalFields([newRow], additionalFieldsKeys);

  dispatch({ type: 'setIsValidating', isValidating: true });

  writeChangeData(actionId, () =>
    entityChangesAddOrUpdateData({
      path: { actionId: actionId },
      body: { data: cloneDataRows },
    }),
  )
    .then((validation: ChangeDataValidation) => {
      dispatch({ type: 'setDataSource', dataSource: newDataSource });
      dispatchValidation(actionId, dispatch, validation);
    })
    .catch((err: OhpenValidationError) => {
      notification().error({ title: getApiErrorMessage(err) });
      console.error(err);
      settleValidating(actionId, dispatch);
    });
};

export const onDeleteRow = <EntityCrudData>({
  entityType,
  actionId,
  dataSource,
  rowNumber,
  dispatch,
  notification,
  t,
}: OnDeleteRowProps<EntityCrudData>) => {
  if (!actionId) {
    notification().error({ title: t('global.errors.unexpected') });
    return;
  }
  const newDataSource = dataSource?.filter(row => row.rowNumber !== rowNumber) ?? [];
  const deleteData = getEntityChangesDeleteData(entityType);

  if (!deleteData) {
    notification().error({ title: t('global.errors.unexpected') });
    return;
  }

  dispatch({ type: 'setIsValidating', isValidating: true });

  writeChangeData(actionId, () => deleteData({ path: { actionId: actionId, rowNumbers: [rowNumber] } }))
    .then((validation: ChangeDataValidation) => {
      dispatch({ type: 'setDataSource', dataSource: newDataSource });
      dispatchValidation(actionId, dispatch, validation);
    })
    .catch((err: OhpenValidationError) => {
      notification().error({ title: getApiErrorMessage(err) });
      console.error(err);
      settleValidating(actionId, dispatch);
    });
};

export const onGoNext = ({ focusSequence, activeCell, dispatch }: OnGoNextProps) => {
  const activeCellIndex = focusSequence.findIndex(
    cell => cell?.rowNumber === activeCell?.rowNumber && cell?.columnKey === activeCell?.columnKey,
  );
  const nextCell: TableActiveCell = focusSequence[activeCellIndex + 1] ?? null;
  dispatch({ type: 'setActiveCell', activeCell: nextCell });
};

export const onGoPrev = ({ focusSequence, activeCell, dispatch }: OnGoPrevProps) => {
  const activeCellIndex = focusSequence.findIndex(
    cell => cell?.rowNumber === activeCell?.rowNumber && cell?.columnKey === activeCell?.columnKey,
  );
  const prevCell: TableActiveCell = focusSequence[activeCellIndex - 1] ?? null;
  dispatch({ type: 'setActiveCell', activeCell: prevCell });
};

export const getProductColumnEditableByStatus = ({
  status,
  columnKey,
  enableEditingLiveProducts,
}: GetColumnEditableByStatusProps): boolean => {
  switch (status) {
    case 'new':
      return true;
    case 'inactive':
      return columnKey !== 'code';
    case 'active':
      return enableEditingLiveProducts || editableIfActive.includes(columnKey);
    case 'withdrawn':
      return editableIfWithdrawn.includes(columnKey);
    case 'cutOff':
      return false;
  }
};

const getColumnEditable = <EntityCrudData>({
  entityType,
  row,
  key,
  isNew,
  isUsed,
  idKey,
  nameKey,
  nonEditableKeys,
}: GetColumnEditableProps<EntityCrudData>): boolean => {
  if (entityType === ActionEntityType.BASE_RATES && !isNew) {
    return key === nameKey;
  }
  if (key === 'actions') return false;
  if (nonEditableKeys.includes(key)) return false;
  if (key === idKey && !isNew) return false;
  if (key !== nameKey && !isNew && isUsed) return false;
  return true;
};

const entityKeysMap: Partial<Record<ActionEntityType, { id: string; name: string; nonEditable?: string[] }>> = {
  [ActionEntityType.PRODUCTS]: { id: 'code', name: 'name', nonEditable: ['replacesCode', 'replacedByCode'] },
  [ActionEntityType.ERC_PROFILES]: { id: 'ercProfileId', name: 'name' },
  [ActionEntityType.FEES]: { id: 'feeId', name: 'feeName' },
  [ActionEntityType.FEE_PROFILES]: { id: 'feeProfileId', name: 'feeProfileName' },
  [ActionEntityType.LENDER_FIRMS]: {
    id: 'firmId',
    name: 'firmName',
    nonEditable: [
      'id',
      'type',
      'frn',
      'operationsWhitelist',
      'idsOfExclusivitySets',
      'packagerFirmIds',
    ],
  },
  [ActionEntityType.BROKERS]: { id: 'id', name: 'name', nonEditable: ['id', 'roles'] },
  [ActionEntityType.LENDERS]: { id: 'id', name: 'id', nonEditable: ['id'] },
  [ActionEntityType.EXCHANGE_RATES]: { id: 'sourceCurrency', name: 'targetCurrency', nonEditable: ['sourceCurrency', 'targetCurrency'] },
  [ActionEntityType.BASE_RATES]: { id: 'code', name: 'name' },
  [ActionEntityType.INTEREST_RATES]: { id: 'baseRateCode', name: 'name', nonEditable: ['baseRateCode', 'index'] },
  [ActionEntityType.EXCLUSIVITY_SETS]: { id: 'id', name: 'name', nonEditable: ['id'] },
  [ActionEntityType.PROCURATION_FEE_RULES]: { id: 'id', name: 'name', nonEditable: ['id'] },
  [ActionEntityType.PROCURATION_FEES]: { id: 'id', name: 'name', nonEditable: ['id', 'index'] },
  [ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS]: { id: 'id', name: 'name', nonEditable: ['id', 'index'] },
  [ActionEntityType.FIRM_PROCURATION_FEE_ASSIGNMENTS]: { id: 'id', name: 'name', nonEditable: ['id', 'index', 'firmId'] },
};

export const getEditableCells = <EntityData, EntityCrudData>({
  entityType,
  actionType,
  mode,
  columnKeys,
  originalDataSource,
  dataSource,
  isImport,
  allEditable,
  enableEditingLiveProducts,
}: GetEditableCellsProps<EntityData, EntityCrudData>) => {
  if (!entityType || !originalDataSource || !dataSource) return {};

  if (entityType === ActionEntityType.PRODUCTS) {
    if (allEditable)
      return dataSource.reduce(
        (obj, row) => ({
          ...obj,
          [row.rowNumber]: columnKeys.reduce(
            (acc, key) => {
              if (typeof key === 'string' && key && key !== 'actions') {
                acc[key] = key !== 'actions' && key !== 'replacesCode' && key !== 'replacedByCode';
              }
              return acc;
            },
            {} as { [key: string]: boolean },
          ),
        }),
        {},
      );
    return originalDataSource.reduce((obj, row, index) => {
      const isImportCreate = isImport && row.rowNumber !== dataSource?.[index]?.rowNumber;
      const status = isImportCreate ? 'new' : getProductStatus(row as ProductCrudData);
      const editableColumns = columnKeys.reduce(
        (acc, key) => {
          if (typeof key === 'string' && key !== 'actions') {
            acc[key] = getProductColumnEditableByStatus({ status, columnKey: key, enableEditingLiveProducts });
          }
          return acc;
        },
        {} as { [key: string]: boolean },
      );
      return { ...obj, [row.rowNumber]: editableColumns };
    }, {});
  }

  const idKey = entityKeysMap[entityType]?.id || 'id';
  const nameKey = entityKeysMap[entityType]?.name || 'name';
  const reorderNonEditable = ['id', 'procurationFeeRuleId', 'procurationFeeId', 'firmId'];
  const nonEditableKeys = actionType === CrudMode.REORDER ? reorderNonEditable : entityKeysMap[entityType]?.nonEditable || [];

  return dataSource.reduce((obj, row, index) => {
    const isUsed = (row as WithIsUsed<WithRowNumber<EntityCrudData>>).isUsed ?? false;
    const isNew =
      actionType === CrudMode.CREATE ||
      actionType === CrudMode.CLONE ||
      actionType === CrudMode.BULK_CLONE ||
      (actionType === CrudMode.IMPORT &&
        (originalDataSource[index] === undefined ||
          row[idKey as keyof typeof row] !== originalDataSource[index][idKey as keyof typeof row]));

    const editableColumns = columnKeys.reduce(
      (acc, key) => {
        if (typeof key === 'string') {
          acc[key] = getColumnEditable({ entityType, row, key, isNew, isUsed, idKey, nameKey, nonEditableKeys });
        }
        return acc;
      },
      {} as { [key: string]: boolean },
    );
    return { ...obj, [row.rowNumber]: editableColumns };
  }, {});
};

export const getTitle = (entityType: ActionEntityType | null, mode: CrudMode | null) => {
  const titles: { [key: string]: string } = {
    [CrudMode.CREATE]: 'global.labels.createX',
    [CrudMode.CLONE]: 'global.labels.cloneX',
    [CrudMode.EDIT]: 'global.labels.editX',
    [CrudMode.BULK_EDIT]: 'global.labels.bulkEditX',
    [CrudMode.BULK_CLONE]: 'global.labels.bulkCloneX',
    [CrudMode.REVIEW]: 'global.labels.reviewXChangeRequest',
    [CrudMode.VIEW]: 'global.labels.viewXChangeRequest',
    [CrudMode.UPDATE_CHANGE]: 'global.labels.editXChangeRequest',
    [CrudMode.UPDATE_IMPORT_CHANGE]: 'global.labels.editXImportChangeRequest',
    [CrudMode.VIEW_RESULT]: 'global.labels.viewXChangeRequestResult',
  };

  return mode ? t(titles[mode], { x: entityType ? t(`pmp.entities.${entityType}`) : '' }) : '';
};

export const getProductStatus = (record: ProductCrudData): ProductStatus => {
  const today = dayjs();

  if (record.productCutoffDate && dayjs(record.productCutoffDate).isSameOrBefore(today, 'day')) return 'cutOff';
  if (record.endDate && dayjs(record.endDate).isBefore(today, 'day')) return 'withdrawn';
  if (record.startDate && dayjs(record.startDate).isSameOrBefore(today, 'day')) return 'active';
  if (record.startDate) return 'inactive';
  return 'new';
};

export const flattenEntityCrudAdditionalFields = <EntityChangeDataRow, EntityCrudData>(
  row: WithAdditionalFields<EntityChangeDataRow>[],
): EntityCrudData[] => {
  return row.map(
    ({ additionalFields, ...coreData }) =>
      ({
        ...coreData,
        ...additionalFields,
      }) as EntityCrudData,
  );
};

export const unflattenEntityCrudAdditionalFields = <EntityCrudData, EntityChangeDataRow>(
  rows: EntityCrudData[],
  additionalFieldsKeys?: string[],
): EntityChangeDataRow[] => {
  return JSON.parse(JSON.stringify(rows)).map((row: EntityCrudData) => {
    const additionalFields = additionalFieldsKeys?.reduce(
      (acc, key) => {
        if (row[key as keyof EntityCrudData] !== undefined) {
          acc[key] = row[key as keyof EntityCrudData];
          delete row[key as keyof EntityCrudData];
        }
        return acc;
      },
      {} as Record<string, unknown>,
    );

    return {
      ...flattenObjectValues(row as unknown as Record<string, unknown>),
      additionalFields,
    } as EntityChangeDataRow;
  });
};

const intermediaryPersonalDataKeys = [
  'title', 'firstName', 'lastName',
  'birthDate', 'mobilePhoneNumber', 'workPhoneNumber',
  'previousName',
];

export const flattenLenderFirmAddress = <T>(rows: T[]): T[] =>
  rows.map(row => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const r = row as any;
    if (!r.firmAddress) return row;
    const { firmAddress, ...rest } = r;
    return {
      ...rest,
      firmAddress,
      firmAddressNumberOrBuilding: firmAddress.numberOrBuilding ?? null,
      firmAddressCity: firmAddress.city ?? null,
      firmAddressPostCode: firmAddress.postCode ?? null,
      firmAddressCountry: firmAddress.country ?? null,
      firmAddressLine1: firmAddress.lineItems?.[0] ?? null,
      firmAddressLine2: firmAddress.lineItems?.[1] ?? null,
      firmAddressLine3: firmAddress.lineItems?.[2] ?? null,
      firmAddressLine4: firmAddress.lineItems?.[3] ?? null,
      firmAddressLine5: firmAddress.lineItems?.[4] ?? null,
    } as T;
  });

const nestLenderFirmAddress = <T>(rows: T[]): T[] =>
  rows.map(row => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const r = row as any;
    const { firmAddressNumberOrBuilding, firmAddressCity, firmAddressPostCode, firmAddressCountry,
      firmAddressLine1, firmAddressLine2, firmAddressLine3, firmAddressLine4, firmAddressLine5, ...rest } = r;
    const hasAny = [firmAddressNumberOrBuilding, firmAddressCity, firmAddressPostCode, firmAddressCountry,
      firmAddressLine1, firmAddressLine2, firmAddressLine3, firmAddressLine4, firmAddressLine5]
      .some(v => v !== undefined && v !== null);
    const lineItems = [firmAddressLine1, firmAddressLine2, firmAddressLine3, firmAddressLine4, firmAddressLine5]
      .map((v: string | null | undefined) => v ?? null);
    return {
      ...rest,
      firmAddress: hasAny
        ? {
            numberOrBuilding: firmAddressNumberOrBuilding ?? null,
            city: firmAddressCity ?? null,
            postCode: firmAddressPostCode ?? null,
            country: firmAddressCountry ?? null,
            lineItems,
          }
        : (rest.firmAddress ?? null),
    } as T;
  });

const LENDER_PERMISSION_STATUSES = [ComplianceStatus.PENDING, ComplianceStatus.FAILED, ComplianceStatus.PASSED];

const permissionArrayToColumns = (
  keyFn: (status: ComplianceStatus) => string,
  data?: Array<{ complianceStatus?: ComplianceStatus | null; permissionLevel?: CompliancePermissionLevel | null }> | null,
): Record<string, CompliancePermissionLevel | null> => {
  const byStatus = new Map((data ?? []).map(entry => [entry.complianceStatus, entry.permissionLevel]));
  return LENDER_PERMISSION_STATUSES.reduce(
    (acc, status) => ({ ...acc, [keyFn(status)]: byStatus.get(status) ?? null }),
    {} as Record<string, CompliancePermissionLevel | null>,
  );
};

const columnsToPermissionArray = (
  keyFn: (status: ComplianceStatus) => string,
  row: Record<string, unknown>,
): Array<{ complianceStatus: ComplianceStatus; permissionLevel: CompliancePermissionLevel }> =>
  LENDER_PERMISSION_STATUSES.filter(status => row[keyFn(status)]).map(status => ({
    complianceStatus: status,
    permissionLevel: row[keyFn(status)] as CompliancePermissionLevel,
  }));

// Lender's draft has no relational sub-object like firmAddress/personalData above - the two
// permission-per-status arrays are flattened onto the row so the grid/tabs can show plain columns.
// Address isn't touched here: it isn't editable anywhere in the Lender UI, so it passes through as-is.
export const flattenLenderDraft = <T>(rows: T[]): T[] =>
  rows.map(row => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const r = row as any;
    return {
      ...r,
      ...permissionArrayToColumns(lenderFirmPermissionColumnKey, r.firmPermissionLevelPerStatus),
      ...permissionArrayToColumns(lenderIntermediaryPermissionColumnKey, r.intermediaryPermissionLevelPerStatus),
    } as T;
  });

const nestLenderDraft = <T>(rows: T[]): T[] =>
  rows.map(row => {
    const r = { ...(row as Record<string, unknown>) };
    const firmPermissionLevelPerStatus = columnsToPermissionArray(lenderFirmPermissionColumnKey, r);
    const intermediaryPermissionLevelPerStatus = columnsToPermissionArray(lenderIntermediaryPermissionColumnKey, r);

    for (const key of [
      ...LENDER_PERMISSION_STATUSES.map(lenderFirmPermissionColumnKey),
      ...LENDER_PERMISSION_STATUSES.map(lenderIntermediaryPermissionColumnKey),
    ]) {
      delete r[key];
    }

    return { ...r, firmPermissionLevelPerStatus, intermediaryPermissionLevelPerStatus } as T;
  });

export const flattenLenderFirmProcFeeBoost = <T>(rows: T[]): T[] =>
  rows.map(row => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const r = row as any;
    if (!r.procFeeBoost) return row;
    const { procFeeBoost, ...rest } = r;
    return {
      ...rest,
      procFeeBoost,
      procFeeBoostValue: procFeeBoost.value ?? null,
      procFeeBoostUnit: procFeeBoost.unit ?? null,
    } as T;
  });

const nestLenderFirmProcFeeBoost = <T>(rows: T[]): T[] =>
  rows.map(row => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const r = row as any;
    if (!('procFeeBoostValue' in r) && !('procFeeBoostUnit' in r)) return row;
    const { procFeeBoostValue, procFeeBoostUnit, ...rest } = r;
    const hasAny = [procFeeBoostValue, procFeeBoostUnit].some(v => v !== undefined && v !== null);
    return {
      ...rest,
      procFeeBoost: hasAny
        ? {
            value: procFeeBoostValue ?? null,
            unit: procFeeBoostUnit ?? null,
          }
        : null,
    } as T;
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getColumnsByEntityType(entityType: ActionEntityType | null): TableColumn<any>[] | null {
  if (entityType === ActionEntityType.PRODUCTS) return productsColumns;
  if (entityType === ActionEntityType.ERC_PROFILES) return ercProfilesFlatColumns;
  if (entityType === ActionEntityType.FEES) return feesFlatColumns;
  if (entityType === ActionEntityType.FEE_PROFILES) return feeProfilesColumns;
  if (entityType === ActionEntityType.EXCHANGE_RATES) return exchangeRateChangeColumns;
  if (entityType === ActionEntityType.BASE_RATES) return baseRateChangeColumns;
  if (entityType === ActionEntityType.INTEREST_RATES) return interestRateColumns;
  if (entityType === ActionEntityType.EXCLUSIVITY_SETS) return exclusiveSetsCRColumns;
  if (entityType === ActionEntityType.PROCURATION_FEE_RULES) return procurationFeeRuleColumns;
  if (entityType === ActionEntityType.PROCURATION_FEES) return procurationFeeCrColumns;
  if (entityType === ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS) return procurationFeeAssignmentsCRColumns;
  if (entityType === ActionEntityType.FIRM_PROCURATION_FEE_ASSIGNMENTS) return firmProcurationFeeCRColumns;
  if (entityType === ActionEntityType.LENDER_FIRMS) return lenderFirmCRColumns;
  if (entityType === ActionEntityType.LENDERS) return lenderCRColumns;
  if (entityType === ActionEntityType.BROKERS) return intermediaryCRColumns;
  return null;
}

export const LENDER_FIRM_CONTACT_DETAIL_COLUMN_KEYS: string[] = [
  'email', 'mobilePhone', 'workPhone', 'website', 'complaintsWebsite', 'sendCaseUpdatesToEmailAllowed',
];

export const LENDER_FIRM_ADDRESS_COLUMN_KEYS: string[] = [
  'firmAddressNumberOrBuilding', 'firmAddressCity', 'firmAddressPostCode', 'firmAddressCountry',
  'firmAddressLine1', 'firmAddressLine2', 'firmAddressLine3', 'firmAddressLine4', 'firmAddressLine5',
];

export const LENDER_FIRM_COMPLIANCE_COLUMN_KEYS: string[] = ['complianceStatus', 'onTheWatchlist'];

export const LENDER_FIRM_PROC_FEE_BOOST_COLUMN_KEYS: string[] = ['procFeeBoostValue', 'procFeeBoostUnit'];

export const expandLenderFirmRowFeedback = (
  feedback: ChangeRequestRowFeedback | undefined,
  row: Record<string, unknown>,
  originalRow: Record<string, unknown> | undefined,
): ChangeRequestRowFeedback | undefined => {
  if (!feedback?.cellsFeedback) return feedback;
  const expanded = { ...feedback.cellsFeedback };
  const contactFeedback = feedback.cellsFeedback['contactDetails'];
  if (contactFeedback) {
    for (const key of LENDER_FIRM_CONTACT_DETAIL_COLUMN_KEYS) {
      expanded[key] = { ...contactFeedback, modified: (row[key] ?? null) !== (originalRow?.[key] ?? null) };
    }
  }
  const addressFeedback = feedback.cellsFeedback['firmAddress'];
  if (addressFeedback) {
    for (const key of LENDER_FIRM_ADDRESS_COLUMN_KEYS) {
      expanded[key] = { ...addressFeedback, modified: (row[key] ?? null) !== (originalRow?.[key] ?? null) };
    }
  }
  const complianceFeedback = feedback.cellsFeedback['compliance'];
  if (complianceFeedback) {
    for (const key of LENDER_FIRM_COMPLIANCE_COLUMN_KEYS) {
      expanded[key] = { ...complianceFeedback, modified: (row[key] ?? null) !== (originalRow?.[key] ?? null) };
    }
  }
  const procFeeBoostFeedback = feedback.cellsFeedback['procFeeBoost'];
  if (procFeeBoostFeedback) {
    for (const key of LENDER_FIRM_PROC_FEE_BOOST_COLUMN_KEYS) {
      expanded[key] = { ...procFeeBoostFeedback, modified: (row[key] ?? null) !== (originalRow?.[key] ?? null) };
    }
  }
  return { ...feedback, cellsFeedback: expanded };
};

export const INTERMEDIARY_COMPLIANCE_COLUMN_KEYS: string[] = ['complianceStatus'];

export const expandIntermediaryRowFeedback = (
  feedback: ChangeRequestRowFeedback | undefined,
  row: Record<string, unknown>,
  originalRow: Record<string, unknown> | undefined,
): ChangeRequestRowFeedback | undefined => {
  if (!feedback?.cellsFeedback) return feedback;
  const expanded = { ...feedback.cellsFeedback };
  const personalDataFeedback = feedback.cellsFeedback['personalData'];
  if (personalDataFeedback) {
    for (const key of intermediaryPersonalDataKeys) {
      expanded[key] = { ...personalDataFeedback, modified: (row[key] ?? null) !== (originalRow?.[key] ?? null) };
    }
  }
  const complianceFeedback = feedback.cellsFeedback['compliance'];
  if (complianceFeedback) {
    for (const key of INTERMEDIARY_COMPLIANCE_COLUMN_KEYS) {
      expanded[key] = { ...complianceFeedback, modified: (row[key] ?? null) !== (originalRow?.[key] ?? null) };
    }
  }
  return { ...feedback, cellsFeedback: expanded };
};

export const expandLenderRowFeedback = (
  feedback: ChangeRequestRowFeedback | undefined,
  row: Record<string, unknown>,
  originalRow: Record<string, unknown> | undefined,
): ChangeRequestRowFeedback | undefined => {
  if (!feedback?.cellsFeedback) return feedback;
  const expanded = { ...feedback.cellsFeedback };
  const firmPermissionFeedback = feedback.cellsFeedback['firmPermissionLevelPerStatus'];
  if (firmPermissionFeedback) {
    for (const status of LENDER_PERMISSION_STATUSES) {
      const key = lenderFirmPermissionColumnKey(status);
      expanded[key] = { ...firmPermissionFeedback, modified: (row[key] ?? null) !== (originalRow?.[key] ?? null) };
    }
  }
  const intermediaryPermissionFeedback = feedback.cellsFeedback['intermediaryPermissionLevelPerStatus'];
  if (intermediaryPermissionFeedback) {
    for (const status of LENDER_PERMISSION_STATUSES) {
      const key = lenderIntermediaryPermissionColumnKey(status);
      expanded[key] = { ...intermediaryPermissionFeedback, modified: (row[key] ?? null) !== (originalRow?.[key] ?? null) };
    }
  }
  return { ...feedback, cellsFeedback: expanded };
};

export const buildSubTableEditableCells = (
  rows: Array<{ rowNumber: number }>,
  columnKeys: string[],
): TableEditableCells =>
  rows.reduce(
    (acc, row) => ({
      ...acc,
      [row.rowNumber]: columnKeys.reduce((cols, key) => ({ ...cols, [key]: true }), {} as Record<string, boolean>),
    }),
    {} as TableEditableCells,
  );

export const buildTradingAddressOriginalDataObject = (
  tradingAddressRows: TradingAddressRow[],
  originalFirms: LenderFirmCRDataRow[],
): Record<string, TradingAddressRow> => {
  const acc: Record<string, TradingAddressRow> = {};
  for (const row of tradingAddressRows) {
    const originalFirm = originalFirms.find(f => f.rowNumber === row.firmRowNumber);
    const originalAddresses = originalFirm?.tradingAddresses ?? [];
    const positionInFirm = tradingAddressRows.filter(
      r => r.firmRowNumber === row.firmRowNumber && r.rowNumber < row.rowNumber,
    ).length;
    const originalAddr = row.id
      ? originalAddresses.find(a => a.id === row.id)
      : originalAddresses[positionInFirm];
    acc[row.rowNumber] = originalAddr
      ? {
          ...originalAddr,
          id: originalAddr.id ?? null,
          name: originalAddr.name ?? null,
          numberOrBuilding: originalAddr.numberOrBuilding ?? null,
          city: originalAddr.city ?? null,
          postCode: originalAddr.postCode ?? null,
          country: originalAddr.country ?? null,
          isActive: originalAddr.isActive ?? null,
          businessDevelopmentManager: originalAddr.businessDevelopmentManager ?? null,
          rowNumber: row.rowNumber,
          firmRowNumber: row.firmRowNumber,
          firmName: originalFirm?.name ?? row.firmName,
          line1: originalAddr.lineItems?.[0] ?? null,
          line2: originalAddr.lineItems?.[1] ?? null,
          line3: originalAddr.lineItems?.[2] ?? null,
          line4: originalAddr.lineItems?.[3] ?? null,
          line5: originalAddr.lineItems?.[4] ?? null,
        }
      : {
          rowNumber: row.rowNumber,
          firmRowNumber: row.firmRowNumber,
          firmName: row.firmName,
          id: null,
          name: null,
          numberOrBuilding: null,
          city: null,
          postCode: null,
          country: null,
          isActive: null,
          businessDevelopmentManager: null,
          line1: null,
          line2: null,
          line3: null,
          line4: null,
          line5: null,
        };
  }
  return acc;
};

export const buildOperationsWhitelistOriginalDataObject = (
  operationsWhitelistRows: OperationWhitelistRow[],
  originalFirms: LenderFirmCRDataRow[],
): Record<string, OperationWhitelistRow> => {
  const acc: Record<string, OperationWhitelistRow> = {};
  for (const row of operationsWhitelistRows) {
    const originalFirm = originalFirms.find(f => f.rowNumber === row.firmRowNumber);
    // Rows are built via sortOperationsByType; originals must be sorted identically because matching is positional.
    const originalOps = sortOperationsByType(originalFirm?.operationsWhitelist ?? []);
    const positionInFirm = operationsWhitelistRows.filter(
      r => r.firmRowNumber === row.firmRowNumber && r.rowNumber < row.rowNumber,
    ).length;
    const originalOp = originalOps[positionInFirm];
    acc[row.rowNumber] = originalOp
      ? {
          rowNumber: row.rowNumber,
          firmRowNumber: row.firmRowNumber,
          firmName: originalFirm?.name ?? row.firmName,
          type: originalOp.type,
          accountName: originalOp.bankAccountDetails?.accountName ?? null,
          accountNumber: originalOp.bankAccountDetails?.accountNumber ?? null,
          sortCode: originalOp.bankAccountDetails?.sortCode ?? null,
          bankName: originalOp.bankAccountDetails?.bankName ?? null,
        }
      : ({ rowNumber: row.rowNumber, firmRowNumber: row.firmRowNumber, firmName: row.firmName } as OperationWhitelistRow);
  }
  return acc;
};

export const buildOriginalDataObject = <T extends { rowNumber: number }>(rows: T[] | null | undefined): Record<string, T> => {
  if (!rows) return {};
  const acc: Record<string, T> = {};
  for (const row of rows) {
    acc[row.rowNumber] = row;
  }
  return acc;
};

// Sub-table feedback from the backend is emitted at the array level (a single entry keyed by the
// collection name, e.g. 'tradingAddresses'). Reduce a KeyValue to its key so a raw enum row value
// compares equal to a mapped {key,value} original.
const normalizeSubTableCellValue = (value: unknown): unknown =>
  value && typeof value === 'object' && 'key' in value ? ((value as { key?: unknown }).key ?? null) : (value ?? null);

export const buildSubTableDataSource = <T extends { rowNumber: number }>(
  rows: T[],
  columnKeys: string[],
  feedbackCellKey: string,
  validationFeedback: ValidationFeedback | null | undefined,
  getParentRowNumber: (row: T) => number,
  getActiveColumnKey?: (rowNumber: number) => string | null,
  originalDataObject?: Record<string, T>,
): Array<T & { rowFeedback?: ChangeRequestRowFeedback; activeColumnKey?: string | null }> =>
  rows.map(row => {
    const parentRowNumber = getParentRowNumber(row);
    const parentFeedback = validationFeedback?.[parentRowNumber];
    const cellFeedback = parentFeedback?.cellsFeedback?.[feedbackCellKey];
    // The array-level `modified` flag is true whenever any field of any row in the collection changed.
    // Fan it out per column while recomputing `modified` from an actual per-field diff so only the
    // cells that really changed are highlighted.
    const originalRow = originalDataObject?.[row.rowNumber] as Record<string, unknown> | undefined;
    const rowValues = row as Record<string, unknown>;
    const rowFeedback: ChangeRequestRowFeedback | undefined = cellFeedback
      ? {
          errors: parentFeedback?.errors ?? [],
          cellsFeedback: columnKeys.reduce<Record<string, ChangeRequestCellFeedback>>(
            (acc, key) => ({
              ...acc,
              [key]: originalDataObject
                ? {
                    ...cellFeedback,
                    modified:
                      normalizeSubTableCellValue(rowValues[key]) !== normalizeSubTableCellValue(originalRow?.[key]),
                  }
                : cellFeedback,
            }),
            {},
          ),
        }
      : undefined;
    return {
      ...row,
      ...(getActiveColumnKey ? { activeColumnKey: getActiveColumnKey(row.rowNumber) } : {}),
      rowFeedback,
    };
  });

export const changeCloneType = (actionId: string | null, notification: () => NotificationInstance) => {
  if (!actionId) {
    notification().error({ title: t('global.errors.unexpected') });
    return;
  }

  return changeRequestFlipBetweenCloneAndReplace({ path: { changeRequestId: actionId } })
    .catch((err: OhpenValidationError) => {
      notification().error({ title: getApiErrorMessage(err) });
      console.error(err);
    })
    .finally(() => {
      return Promise.resolve();
    });
};
